import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Eye } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const emptyFw = { name: '', tagline: '', summary: '', body: '', tags: '', reading_time: 5 };

export default function AdminFrameworks() {
  const [frameworks, setFrameworks] = useState([]);
  const [form, setForm] = useState(emptyFw);
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = () => api.get('/admin/frameworks').then(res => setFrameworks(res.data)).catch(console.error);
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
        reading_time: parseInt(form.reading_time) || 5
      };
      if (editId) {
        await api.put(`/admin/frameworks/${editId}`, payload);
        toast.success('Framework updated.');
      } else {
        await api.post('/admin/frameworks', payload);
        toast.success('Framework created.');
      }
      setDialogOpen(false);
      setForm(emptyFw);
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save.');
    }
  };

  const handleEdit = (fw) => {
    setEditId(fw._id);
    setForm({
      name: fw.name, tagline: fw.tagline || '', summary: fw.summary || '',
      body: fw.body || '', tags: fw.tags?.join(', ') || '', reading_time: fw.reading_time || 5
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this framework?')) return;
    try {
      await api.delete(`/admin/frameworks/${id}`);
      toast.success('Framework deleted.');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete.');
    }
  };

  return (
    <AdminShell title="Frameworks">
      <div className="flex items-center justify-between mb-6">
        <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
          {frameworks.length} framework{frameworks.length !== 1 ? 's' : ''}
        </p>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setForm(emptyFw); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm" data-testid="admin-framework-create-button">
              <Plus className="h-4 w-4 mr-2" /> New Framework
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
                {editId ? 'Edit Framework' : 'New Framework'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Name</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Tagline</label>
                <Input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Summary</label>
                <Textarea value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} rows={2} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Body (Markdown)</label>
                <Textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={10} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Tags (comma-separated)</label>
                  <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Reading Time (min)</label>
                  <Input type="number" value={form.reading_time} onChange={e => setForm({...form, reading_time: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
              </div>
              <Button onClick={handleSave} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg" data-testid="admin-framework-save-button">
                {editId ? 'Update Framework' : 'Create Framework'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Name</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Tagline</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Tags</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {frameworks.map(fw => (
              <TableRow key={fw._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                <TableCell className="font-['Cormorant_Garamond'] text-base font-semibold text-[hsl(var(--foreground))]">{fw.name}</TableCell>
                <TableCell className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{fw.tagline}</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {fw.tags?.slice(0, 2).map(t => (
                      <Badge key={t} variant="outline" className="font-['Inter'] text-[9px] uppercase border-[rgba(168,181,178,0.15)] text-[hsl(var(--muted-foreground))]">{t}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`/work/${fw.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]" onClick={() => handleEdit(fw)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-red-400" onClick={() => handleDelete(fw._id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminShell>
  );
}
